import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Heart, Check, RotateCcw } from 'lucide-react';

const ORGANS = [
  { id: 'brain', name: 'Brain', correct: 0, description: 'Controls thoughts, memory, and body functions', color: '#FF6B6B' },
  { id: 'lungs', name: 'Lungs', correct: 1, description: 'Helps you breathe by exchanging oxygen and CO2', color: '#4D96FF' },
  { id: 'heart', name: 'Heart', correct: 2, description: 'Pumps blood throughout your body', color: '#FF6B6B' },
  { id: 'stomach', name: 'Stomach', correct: 3, description: 'Breaks down food using acids and enzymes', color: '#FFC03D' },
  { id: 'liver', name: 'Liver', correct: 4, description: 'Filters blood and produces bile for digestion', color: '#A493FF' },
  { id: 'kidneys', name: 'Kidneys', correct: 5, description: 'Filters waste from blood to make urine', color: '#38E54D' },
];

const BODY_SLOTS = [
  { id: 0, label: 'Head', y: 30, desc: 'Top of the body' },
  { id: 1, label: 'Chest (Left & Right)', y: 120, desc: 'Upper torso' },
  { id: 2, label: 'Chest (Center)', y: 160, desc: 'Center of chest' },
  { id: 3, label: 'Upper Abdomen', y: 210, desc: 'Below the chest' },
  { id: 4, label: 'Right Abdomen', y: 250, desc: 'Right side of belly' },
  { id: 5, label: 'Lower Back', y: 290, desc: 'Lower abdomen area' },
];

const BodyExplorer = () => {
  const navigate = useNavigate();
  const [placements, setPlacements] = useState({});
  const [selectedOrgan, setSelectedOrgan] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [score, setScore] = useState(0);
  const [checked, setChecked] = useState(false);
  const availableOrgans = ORGANS.filter(o => !Object.values(placements).includes(o.id));

  const placeOrgan = (slotId) => {
    if (checked || !selectedOrgan) return;
    const newPlacements = { ...placements };
    // Remove organ from previous slot
    Object.keys(newPlacements).forEach(k => { if (newPlacements[k] === selectedOrgan) delete newPlacements[k]; });
    newPlacements[slotId] = selectedOrgan;
    setPlacements(newPlacements);
    setSelectedOrgan(null);
  };

  const checkAnswers = () => {
    let correct = 0;
    ORGANS.forEach(organ => {
      const slotId = Object.keys(placements).find(k => placements[k] === organ.id);
      if (slotId !== undefined && parseInt(slotId) === organ.correct) correct++;
    });
    setScore(correct * 15);
    setChecked(true);
    setFeedback(correct === ORGANS.length ? 'Perfect! You know the human body well!' : `${correct}/${ORGANS.length} correct. ${correct >= 4 ? 'Great job!' : 'Keep learning!'}`);
  };

  const reset = () => { setPlacements({}); setSelectedOrgan(null); setFeedback(null); setScore(0); setChecked(false); };

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 to-pink-50" data-testid="body-explorer-game">
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-6">
          <button onClick={() => navigate('/games')} className="flex items-center gap-2 text-[#64748B] font-semibold"><ArrowLeft className="w-5 h-5" />Back</button>
          <span className="bg-[#4D96FF] text-white px-4 py-2 rounded-2xl font-bold">{score}pts</span>
        </div>
        <h2 className="text-3xl font-black text-[#1E293B] text-center mb-6">Place the organs in the correct position!</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Organ Bank */}
          <div className="bg-white rounded-3xl p-6 border-2 border-slate-100 shadow-xl">
            <h3 className="text-xl font-bold text-[#1E293B] mb-4">Organ Bank</h3>
            <div className="space-y-2">
              {availableOrgans.map(organ => (
                <button key={organ.id} onClick={() => setSelectedOrgan(organ.id)} data-testid={`organ-${organ.id}`}
                  className={`w-full p-3 rounded-2xl border-2 font-bold text-left transition-all ${
                    selectedOrgan === organ.id ? 'border-[#4D96FF] bg-blue-50 scale-105' : 'border-slate-200 hover:border-[#4D96FF]'
                  }`}>
                  <span className="inline-block w-3 h-3 rounded-full mr-2" style={{backgroundColor: organ.color}}></span>
                  {organ.name}
                  <p className="text-xs text-[#64748B] font-normal mt-1">{organ.description}</p>
                </button>
              ))}
              {selectedOrgan && <p className="text-sm text-[#4D96FF] font-bold mt-3">Now click a body slot to place it!</p>}
            </div>
          </div>

          {/* Body */}
          <div className="bg-white rounded-3xl p-6 border-2 border-slate-100 shadow-xl">
            <h3 className="text-xl font-bold text-[#1E293B] mb-4 text-center">Human Body</h3>
            <div className="space-y-3">
              {BODY_SLOTS.map(slot => {
                const placedOrgan = placements[slot.id] ? ORGANS.find(o => o.id === placements[slot.id]) : null;
                const isCorrect = checked && placedOrgan && placedOrgan.correct === slot.id;
                const isWrong = checked && placedOrgan && placedOrgan.correct !== slot.id;
                return (
                  <button key={slot.id} onClick={() => placeOrgan(slot.id)} data-testid={`slot-${slot.id}`}
                    className={`w-full p-4 rounded-2xl border-2 text-center transition-all ${
                      isCorrect ? 'bg-green-100 border-green-500' :
                      isWrong ? 'bg-red-100 border-red-500' :
                      placedOrgan ? 'bg-blue-50 border-[#4D96FF]' :
                      selectedOrgan ? 'bg-slate-50 border-dashed border-[#4D96FF] hover:bg-blue-50 cursor-pointer' :
                      'bg-slate-50 border-slate-200'
                    }`}>
                    <p className="text-xs font-semibold text-[#64748B]">{slot.label}</p>
                    {placedOrgan && <p className="font-bold text-lg" style={{color: placedOrgan.color}}>{placedOrgan.name}</p>}
                    {!placedOrgan && <p className="text-slate-300 text-sm">Empty</p>}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Info */}
          <div className="bg-white rounded-3xl p-6 border-2 border-slate-100 shadow-xl">
            <h3 className="text-xl font-bold text-[#1E293B] mb-4">Instructions</h3>
            <ol className="space-y-3 text-[#64748B]">
              <li className="flex gap-2"><span className="bg-[#4D96FF] text-white w-6 h-6 rounded-lg flex items-center justify-center text-xs font-bold flex-shrink-0">1</span>Select an organ from the bank</li>
              <li className="flex gap-2"><span className="bg-[#4D96FF] text-white w-6 h-6 rounded-lg flex items-center justify-center text-xs font-bold flex-shrink-0">2</span>Click a body slot to place it</li>
              <li className="flex gap-2"><span className="bg-[#4D96FF] text-white w-6 h-6 rounded-lg flex items-center justify-center text-xs font-bold flex-shrink-0">3</span>Check your answers when done</li>
            </ol>
            {feedback && <div className={`mt-4 p-4 rounded-2xl font-bold ${score === ORGANS.length * 15 ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`}>{feedback}</div>}
            <div className="mt-4 flex gap-2">
              <button onClick={reset} className="flex-1 py-3 bg-slate-100 rounded-2xl font-bold text-[#64748B] flex items-center justify-center gap-1"><RotateCcw className="w-4 h-4" />Reset</button>
              <button onClick={checkAnswers} disabled={checked || Object.keys(placements).length < ORGANS.length}
                className="flex-1 py-3 bg-[#38E54D] text-white rounded-2xl font-bold border-b-4 border-green-600 active:border-b-0 active:translate-y-1 transition-all disabled:opacity-50 flex items-center justify-center gap-1" data-testid="check-body-btn"><Check className="w-4 h-4" />Check</button>
            </div>
            {checked && <button onClick={() => navigate('/games')} className="w-full mt-3 py-3 bg-rose-500 text-white rounded-2xl font-bold">Back to Brain Arena</button>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BodyExplorer;
